import { Drawer, Flex, Space, Tooltip } from 'antd';
import React, { useMemo } from 'react';
import { Container } from '@/ui';
import ConfigureStepItem from '@/components/common/SidePanels/QuestionnaireSidePanelForm/ConfigureStepItem';
import { useWatch } from 'react-hook-form';
import { kycHelpers } from '@/utils/helpers/kyc';
import { getLocalizedValue } from '@/utils/helpers';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { COLORS } from '@/utils/static';

export const QuestionnaireSidePanelForm = ({
  steps,
  setStep,
  currentStep,
  step,
  isAdmin,
  control,
  isInProcess,
  isOpen,
  onClose,
  isInReview
}) => {
  step = step - 1;

  const watchedFields = useWatch({
    control: control,
    name: steps.flatMap((step) => step.visibleConditions?.map((condition) => condition.id) || [])
  });

  // Mark identical siblings to only show the last step
  const [groupedSteps, displayedSteps, sortedSteps] = useMemo(() => {
    // these get updated by the .map() to avoid another iteration
    let displayCount = 0;
    const skippedSteps = [];
    const sortedSteps = steps
      .sort((a, b) =>
        a.newNumber ? a.newNumber - b.newNumber : a.number ? a.number - b.newNumber : 0
      )
      .filter((item) =>
        kycHelpers.conditioningSteps(watchedFields, item.visibleConditions, item.visibleOperator)
      );
    const sortedStepsSections = sortedSteps.map((item) => item.section);

    const groupedSteps = sortedStepsSections.map((step, index) => {
      if (isAdmin) {
        const hiddenCount = index - displayCount;
        displayCount++;
        return [index, index - hiddenCount, true];
      } else {
        if (skippedSteps.includes(index)) {
          displayCount++;
          return [index, index - 1, null];
        }

        const hiddenCount = index - displayCount;

        if (step !== sortedStepsSections[index + 1]) {
          displayCount++;
          return [index, index - hiddenCount, true];
        }

        let statusIndex = index;
        const indexes = [statusIndex];
        while (
          step === sortedStepsSections[statusIndex + 1] &&
          statusIndex < sortedStepsSections.length
        ) {
          statusIndex++;
          indexes.push(statusIndex);
          skippedSteps.push(index + 1);
        }

        return [
          // The index used for the status in the panel (last of the group)
          indexes,
          // The index used for the display in the panel (first of the group)
          index - hiddenCount,
          // Do not display the step
          false
        ];
      }
    });

    return [groupedSteps, displayCount, sortedSteps];
  }, [steps, watchedFields]);

  return (
    <>
      <Container $mdHidden>
        <SidePanelContent
          groupedSteps={groupedSteps}
          currentStep={currentStep}
          step={step}
          sortedSteps={sortedSteps}
          setStep={setStep}
          isInProcess={isInProcess}
          isAdmin={isAdmin}
        />
      </Container>

      <Drawer placement='left' open={isOpen} onClose={onClose}>
        <SidePanelContent
          groupedSteps={groupedSteps}
          currentStep={currentStep}
          step={step}
          sortedSteps={sortedSteps}
          setStep={setStep}
          isInProcess={isInProcess}
          isAdmin={isAdmin}
        />
      </Drawer>
    </>
  );
};

const SidePanelContent = ({
  groupedSteps,
  currentStep,
  step,
  sortedSteps,
  setStep,
  isInProcess,
  isAdmin
}) => {
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;

  const fixedSections = useMemo(() => {
    return sortedSteps.map(
      (step) =>
        step?.questionGroups?.some((group) =>
          group?.questions?.some((question) => question?.answers?.some((answer) => answer?.isFixed))
        ) || false
    );
  }, [sortedSteps]);

  const editedByAdminSections = useMemo(() => {
    return sortedSteps.map(
      (step) =>
        step?.questionGroups?.some((group) =>
          group?.questions?.some((question) =>
            question?.answers?.some((answer) => answer?.creatorAdmin)
          )
        ) || false
    );
  }, [sortedSteps]);

  return (
    <Space direction='vertical' size={20}>
      {groupedSteps &&
        groupedSteps
          .filter((item) => item[2] !== null)
          .map(([statusIndex, displayIndex, show], index) => (
            <ConfigureStepItem
              key={index}
              currentStep={groupedSteps?.[step]?.[0]}
              initialStep={currentStep ? groupedSteps?.[currentStep]?.[0] : null}
              label={
                <Flex align='center' gap={6}>
                  {Array.isArray(statusIndex)
                    ? getLocalizedValue(currentLanguage, 'section', sortedSteps[statusIndex[0]])
                    : getLocalizedValue(currentLanguage, 'section', sortedSteps[statusIndex])}
                  <Flex align='center' gap={6}>
                    {isAdmin &&
                      (Array.isArray(statusIndex)
                        ? fixedSections[statusIndex[statusIndex.length - 1]]
                        : fixedSections[statusIndex]) && (
                        <Tooltip title={t('Answers in this section were fixed by client')}>
                          <CheckCircleOutlined style={{ color: COLORS.mintGreen }} />
                        </Tooltip>
                      )}
                        {(Array.isArray(statusIndex)
                          ? editedByAdminSections[statusIndex[statusIndex.length - 1]]
                          : editedByAdminSections[statusIndex]) && (
                          <Tooltip title={t('Questions in this section were edited by administator')}>
                            <ExclamationCircleOutlined
                              style={{
                                color: COLORS.aragonBlue
                              }}
                            />
                          </Tooltip>
                        )}
                      </Flex>
                    </Flex>
                  }
                  step={statusIndex}
                  stepNumber={displayIndex + 1}
                  show={show}
                  onClick={setStep ? () => setStep(displayIndex) : null}
                  isInProcess={isInProcess}
                  adminView={isAdmin}
                />
              ))}
        </Space>
  );
};
